import React from 'react';
import { Layout, Menu, Dropdown, Avatar, Button, message, Tag, Badge, Switch } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  HomeOutlined, 
  UserOutlined, 
  LogoutOutlined,
  CrownOutlined,
  TeamOutlined,
  PictureOutlined,
  MessageOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CommentOutlined
} from '@ant-design/icons';
import { getUserInfo, isAuthenticated, isAdmin, clearUserData, getToken, isTokenExpired } from '../../utils/auth';
import { getOnlineStatus, switchOnlineStatus } from '../../api/auth';
import './index.css';

const { Header, Content, Sider } = Layout;

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = getUserInfo();
  const userIsAdmin = isAdmin();
  
  console.log('🎨 MainLayout渲染');
  console.log('  - 当前路径:', location.pathname);
  console.log('  - 用户信息:', userInfo);
  console.log('  - 是否管理员:', userIsAdmin);
  
  const wsRef = React.useRef(null);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [collapsed, setCollapsed] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const [onlineStatus, setOnlineStatus] = React.useState(1); // 0离线，1在线
  const [switchingStatus, setSwitchingStatus] = React.useState(false);

  // 检查登录状态和token过期
  React.useEffect(() => {
    console.log('========================================');
    console.log('🔍 MainLayout初始化检查');
    
    if (!isAuthenticated()) {
      console.log('❌ 未登录，跳转到登录页');
      console.log('========================================');
      navigate('/login');
      return;
    }
    
    console.log('✅ 已登录');
    
    // 检查token是否过期
    if (isTokenExpired()) {
      console.log('❌ token已过期，清除数据并跳转到登录页');
      console.log('========================================');
      message.warning('登录已过期（24小时），请重新登录');
      clearUserData();
      navigate('/login');
    } else {
      console.log('✅ token未过期');
      console.log('========================================');
    }
  }, [navigate]);

  // 定期检查token是否过期（每分钟检查一次）
  React.useEffect(() => {
    const checkInterval = setInterval(() => {
      console.log('⏰ 定期检查token是否过期...');
      if (isTokenExpired()) {
        console.log('❌ token已过期，清除数据并跳转到登录页');
        message.warning('登录已过期（24小时），请重新登录');
        clearUserData();
        navigate('/login');
      }
    }, 60000); // 每分钟检查一次

    return () => clearInterval(checkInterval);
  }, [navigate]);

  // 检测屏幕尺寸，判断是否为移动端
  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // 移动端默认折叠菜单
      if (mobile) {
        setCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 获取在线状态
  const fetchOnlineStatus = async () => {
    try {
      const result = await getOnlineStatus();
      if (result.code === 200 && result.data) {
        setOnlineStatus(result.data.onlineStatus);
      }
    } catch (error) {
      console.error('获取在线状态失败:', error);
    }
  };

  // 初始化时获取在线状态
  React.useEffect(() => {
    if (isAuthenticated()) {
      fetchOnlineStatus();
    }
  }, []);

  // 切换在线状态
  const handleSwitchOnlineStatus = async (checked) => {
    const newStatus = checked ? 1 : 0;
    try {
      setSwitchingStatus(true);
      const result = await switchOnlineStatus(newStatus);
      if (result.code === 200) {
        setOnlineStatus(newStatus);
        message.success(newStatus === 1 ? '已上线' : '已离线');
      } else {
        message.error(result.msg || '切换状态失败');
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      message.error('切换状态失败');
    } finally {
      setSwitchingStatus(false);
    }
  };

  // 建立WebSocket连接并监听新消息
  React.useEffect(() => {
    if (!userInfo?.userId) return;

    const token = getToken();
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//51apt.com/api/websocket/chat/${userInfo.userId}/2?token=${token}`;
    
    console.log('🔌 MainLayout建立WebSocket连接:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ MainLayout WebSocket连接已建立');
        // 连接成功后获取未读消息数
        fetchUnreadCount();
      };

      ws.onmessage = (event) => {
        console.log('📨 MainLayout收到WebSocket消息:', event.data);
        // 收到新消息时更新未读数
        fetchUnreadCount();
      };

      ws.onerror = (error) => {
        console.error('❌ MainLayout WebSocket错误:', error);
      };

      ws.onclose = (event) => {
        console.log('🔌 MainLayout WebSocket连接已关闭');
      };
    } catch (error) {
      console.error('❌ MainLayout创建WebSocket失败:', error);
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [userInfo?.userId]);

  // 获取未读消息总数
  const fetchUnreadCount = async () => {
    try {
      const response = await fetch('/api/manage/chat/sessions', {
        headers: {
          'Authorization': getToken()
        }
      });
      const result = await response.json();
      if (result.code === 200 && result.data) {
        const total = result.data.reduce((sum, session) => sum + (session.unreadCount || 0), 0);
        setUnreadCount(total);
      }
    } catch (error) {
      console.error('获取未读消息数失败:', error);
    }
  };

  const handleLogout = () => {
    clearUserData();
    message.success('退出登录成功');
    navigate('/login');
  };

  // 构建菜单项，根据用户角色显示不同菜单
  const menuItems = [
    {
      key: '/apartments',
      icon: <HomeOutlined />,
      label: '房源管理',
    },
    {
      key: '/messages',
      icon: <MessageOutlined />,
      label: (
        <Badge dot={unreadCount > 0} offset={[10, 0]}>
          用户消息
        </Badge>
      ),
    },
    {
      key: '/phrases',
      icon: <CommentOutlined />,
      label: '常用语设置',
    }
  ];

  // 只有管理员才能看到用户管理和轮播图管理菜单
  if (userIsAdmin) {
    menuItems.push({
      key: '/users',
      icon: <TeamOutlined />,
      label: '用户管理',
    });
    menuItems.push({
      key: '/banners',
      icon: <PictureOutlined />,
      label: '轮播图管理',
    });
  }

  const userMenuItems = [
    {
      key: 'online-status',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 120 }}>
          <span>在线状态</span>
          <Switch
            checked={onlineStatus === 1}
            onChange={handleSwitchOnlineStatus}
            loading={switchingStatus}
            checkedChildren="在线"
            unCheckedChildren="离线"
            size="small"
            onClick={(checked, e) => e.stopPropagation()}
          />
        </div>
      ),
      disabled: true, // 禁用点击，只能通过Switch操作
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  return (
    <Layout className="main-layout">
      <Header className="main-header">
        {/* 移动端汉堡菜单按钮 */}
        {isMobile && (
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '18px',
              width: 48,
              height: 48,
              color: '#fff',
              marginRight: 16
            }}
          />
        )}
        <div className="logo">51APT公寓管理系统</div>
        <div className="user-info">
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div className="user-dropdown">
              <Badge 
                dot 
                status={onlineStatus === 1 ? "success" : "error"} 
                offset={[-5, 5]}
              >
                <Avatar icon={<UserOutlined />} />
              </Badge>
              <span className="user-name">{userInfo?.name || '用户'}</span>
              {userIsAdmin && (
                <Tag icon={<CrownOutlined />} color="gold" style={{ marginLeft: 8 }}>
                  管理员
                </Tag>
              )}
            </div>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Sider 
          width={200} 
          className="site-layout-background"
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="md"
          collapsedWidth={isMobile ? 0 : 200}
          trigger={null}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: isMobile ? 'fixed' : 'relative',
            left: 0,
            top: 64,
            bottom: 0,
            zIndex: isMobile ? 999 : 1,
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => {
              navigate(key);
              // 移动端点击菜单后自动收起
              if (isMobile) {
                setCollapsed(true);
              }
            }}
          />
        </Sider>
        <Layout style={{ padding: '24px', marginLeft: isMobile && !collapsed ? 200 : 0 }}>
          <Content className="site-layout-content">
            <Outlet context={{ wsRef, refreshUnreadCount: fetchUnreadCount }} />
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;



